import { classList } from '@findigs/helpers/src/helpers/class-list';
import type { ElementOrComponent } from '@findigs/helpers/src/helpers/polymorphic';
import { Avatar } from '@findigs/ui-kit/src/components/avatar';
import { IconButton } from '@findigs/ui-kit/src/components/icon-button';
import type { LinkProps } from '@findigs/ui-kit/src/components/link';
import { Link } from '@findigs/ui-kit/src/components/link';
import type { LinkIconProps } from '@findigs/ui-kit/src/components/link-icon';
import { LinkIcon } from '@findigs/ui-kit/src/components/link-icon';
import { Sidebar } from '@findigs/ui-kit/src/components/sidebar';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { FocusScope } from 'react-aria';
import { NavbarPopover, useNavbarPopover } from '~/components/navbar-popover';
import type { NavbarProfileCardProps } from '~/components/navbar-profile-card';
import { NavbarProfileCard } from '~/components/navbar-profile-card';
import { usePreventScroll } from '~/hooks/use-prevent-scroll';
import { useShouldUseFrameworkLink } from '~/hooks/use-should-use-framework-link';
import { LogoLink } from './logo-link';
import {
  linksPopover,
  profilePopover,
  spacer,
  styles,
} from './navbar-pure.module.scss';
import { useNavScroll } from './use-nav-scroll';
import BackIcon from '@findigs/ui-kit/src/icons/arrow-left.inline.svg';
import MenuIcon from '@findigs/ui-kit/src/icons/menu.inline.svg';
import Floop from '@findigs/ui-kit/src/images/logo/floop-bold.inline.svg';
import Logo from '@findigs/ui-kit/src/images/logo/inline-lockup.inline.svg';

export type NavItem = {
  title: string;
  active: boolean;
} & Pick<
  React.HTMLProps<HTMLAnchorElement>,
  'href' | 'rel' | 'target' | 'onClick'
>;

export type NavItemIcon = NavItem & {
  icon: JSX.Element;
};

export type NavbarPureProps<E extends ElementOrComponent> = {
  primaryLinks: NavItemIcon[];
  secondaryLinks?: (NavItem & { alwaysVisible?: boolean })[];
  tertiaryLinks?: NavItem[];
  profile?: NavbarProfileCardProps;

  /**
   * `undefined` if no back button is to be shown, otherwise
   * the `href` and `title` of where it should redirect to.
   */
  backButtonDestination?: { href: string; title: string };

  /**
   * `undefined` if the logo shouldn't be interactive, otherwise
   * the href of where it should redirect to
   */
  logoHref?: string;

  /**
   * The href of where the user should be redirected to for logging out
   */
  logoutHref: string;

  /**
   * The 'Simple' navbar has no profile and links, just the logo
   * @default 'Standard'
   */
  variant?:
    | 'Standard'
    | 'Standard NoPrimary'
    | 'Standard NoPrimary AppFlow'
    | 'Standard NoPrimary Dark'
    | 'Simple'
    | 'Simple Dark';

  /**
   * The framework's built-in `Link` component to be used.
   *
   * If no frameworks are in use, use `a` (anchor tag).
   *
   * @example GatsbyLink, RemixLink, etc
   */
  frameworkLink: E;
  /**
   * The name of the prop that should be passed into the component
   * referenced in `frameworkLink` to specify the destination url.
   *
   * If no frameworks are in use, use `href`.
   * @example For GatsbyLink and RemixLink the prop would be 'to'
   */
  frameworkLinkProp: keyof React.ComponentPropsWithoutRef<E>;
};

type NavLinkProps = LinkProps &
  Partial<Omit<LinkIconProps, keyof LinkProps>> &
  Omit<React.HTMLProps<HTMLAnchorElement>, 'ref' | 'as'>;

export function NavbarPure<E extends ElementOrComponent>({
  primaryLinks,
  secondaryLinks,
  tertiaryLinks,
  backButtonDestination,
  logoHref,
  logoutHref,
  frameworkLink,
  frameworkLinkProp,
  profile,
  variant = 'Standard',
}: NavbarPureProps<E>) {
  const [expanded, setExpanded] = useState(false);
  const navId = useId();
  const ref = useRef<HTMLElement>(null);

  const shouldUseFrameworkLink = useShouldUseFrameworkLink();

  /**
   * This component is an abstraction that will render using either
   * `LinkIcon` or `Link` depending on whether an icon prop is provided
   * Internally it will leverage `polymorphic` to render as `RemixLink`, `<a>` or `<button>` depending on the provided props
   * It forwards its `href` prop to the appropriate prop depending on the
   * component internally used for rendering.
   *
   * External links render using `<a>`, and internal links using
   * `frameworkLink`.
   */
  const NavLink = useCallback(
    ({ href, icon, ...props }: NavLinkProps) => {
      let as: ElementOrComponent = 'a';
      if (href && shouldUseFrameworkLink(href)) {
        as = frameworkLink as ElementOrComponent;
      } else if (!href && props.onClick) {
        as = 'button';
        props.type = props.type ?? 'button';
      }
      const prop = as === 'a' ? 'href' : frameworkLinkProp;

      return icon ? (
        <LinkIcon icon={icon} as={as} {...props} {...{ [prop]: href }} />
      ) : (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <Link as={as} {...props} {...{ [prop]: href }} />
      );
    },
    [frameworkLink, frameworkLinkProp, shouldUseFrameworkLink]
  );

  const onLinkClicked = useCallback(() => {
    setExpanded(false);
  }, []);

  // ensures that the navbar is properly aligned
  useEffect(() => {
    const header = ref.current;
    if (typeof document === 'undefined' || !header) {
      return;
    }

    if (expanded) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    header.scrollTop = 0;
  }, [expanded]);

  usePreventScroll({ isDisabled: !expanded, outerElementRef: ref });

  const linksPopoverProps = useNavbarPopover();
  const profilePopoverProps = useNavbarPopover();

  const scrollPaddingRight =
    profilePopoverProps.scrollPaddingRight ||
    linksPopoverProps.scrollPaddingRight ||
    undefined;

  const onToggle = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const onPopoverItemClicked = () => {
    onLinkClicked();
    linksPopoverProps.state.close();
    profilePopoverProps.state.close();
  };

  const { scroll, spacerRef } = useNavScroll();

  const classesSpacer = classList(spacer, variant, scroll.direction, {
    'back-visible': backButtonDestination,
    scrolled: scroll.isScrolled,
  });

  const classesHeader = classList(
    styles,
    'primary-navbar',
    scroll.direction,
    variant,
    {
      expanded,
      'back-visible': backButtonDestination,
      scrolled: scroll.isScrolled,
    }
  );

  return (
    <FocusScope contain={expanded}>
      <div className={classesSpacer} ref={spacerRef}>
        <Logo className="logo" aria-hidden={true} width="108" height="32" />
      </div>
      <header
        aria-label="Navigation bar"
        className={classesHeader}
        style={
          {
            '--scrollPaddingRight': scrollPaddingRight
              ? `${scrollPaddingRight}px`
              : undefined,
          } as any
        }
        ref={ref}
      >
        {!variant.includes('Simple') ? (
          <>
            <div className="bg">
              <Logo
                className="logo"
                aria-hidden={true}
                width="108"
                height="32"
              />
              <nav id={navId} aria-label="Navigation">
                <ul>
                  {!variant.includes('NoPrimary') && (
                    <>
                      {primaryLinks.map(({ title, active, ...props }) => (
                        <li key={props.href}>
                          <NavLink
                            {...props}
                            onClick={(event) => {
                              props.onClick?.(event);
                              onLinkClicked();
                            }}
                          >
                            {title}
                          </NavLink>
                        </li>
                      ))}
                      {secondaryLinks && (
                        <li className="hr">
                          <hr className="secondary" />
                        </li>
                      )}
                    </>
                  )}
                  {secondaryLinks ? (
                    <>
                      {secondaryLinks.map(
                        ({ title, active, alwaysVisible, ...props }, index) => (
                          <li key={props.href ?? index}>
                            <NavLink
                              {...props}
                              onClick={(event) => {
                                props.onClick?.(event);
                                onLinkClicked();
                              }}
                            >
                              {title}
                            </NavLink>
                          </li>
                        )
                      )}
                    </>
                  ) : null}
                  {tertiaryLinks ? (
                    <>
                      <li className="hr">
                        <hr className="tertiary" />
                      </li>
                      {tertiaryLinks.map(({ title, active, ...props }) => (
                        <li key={props.href} className="tertiary">
                          <NavLink
                            {...props}
                            onClick={(event) => {
                              props.onClick?.(event);
                              onLinkClicked();
                            }}
                          >
                            {title}
                          </NavLink>
                        </li>
                      ))}
                    </>
                  ) : null}
                </ul>
              </nav>
              {profile ? (
                <div className="profile">
                  <NavbarProfileCard
                    id={profile.id}
                    firstName={profile.firstName}
                    lastName={profile.lastName}
                    email={profile.email}
                    onLinkClicked={onLinkClicked}
                  />
                </div>
              ) : null}
            </div>
            {!variant.includes('NoPrimary') && (
              <div className="sidebar grid">
                <Sidebar
                  className="md:col-3 lg:col-4"
                  items={primaryLinks}
                  frameworkLink={frameworkLink}
                  frameworkLinkProp={frameworkLinkProp}
                  shouldUseFrameworkLink={shouldUseFrameworkLink}
                />
              </div>
            )}
          </>
        ) : null}
        <div className="fg">
          {backButtonDestination ? (
            <IconButton
              aria-label={`Go back to ${backButtonDestination.title}`}
              as={NavLink}
              href={backButtonDestination.href}
              onClick={onLinkClicked}
              shape="Circle"
              size="Small"
              className="back"
              variant="StandardNavbar"
              ignoreActiveClass
            >
              <BackIcon aria-hidden="true" width="24" height="24" />
            </IconButton>
          ) : null}
          <LogoLink
            className="logo-link"
            frameworkLink={frameworkLink}
            frameworkLinkProp={frameworkLinkProp}
            href={logoHref}
            onClick={onLinkClicked}
            aria-label="Findigs Logo"
          >
            {backButtonDestination ? (
              <Floop
                className="floop"
                width="27"
                height="40"
                aria-hidden={true}
              />
            ) : null}
            <div className="logo" aria-hidden={true} />
          </LogoLink>
          {variant.includes('Simple') ? (
            <div className="always-visible-links">
              {secondaryLinks
                ?.filter(({ alwaysVisible }) => alwaysVisible)
                .splice(0, 1)
                .map(({ title, active, alwaysVisible, ...props }, index) => (
                  <NavLink
                    key={props.href ?? index}
                    {...props}
                    onClick={(event) => {
                      props.onClick?.(event);
                      onLinkClicked();
                    }}
                  >
                    {title}
                  </NavLink>
                ))}
              {profile ? (
                <NavLink href={logoutHref} onClick={onLinkClicked}>
                  Logout
                </NavLink>
              ) : null}
            </div>
          ) : (
            <div className="floating-actions">
              <IconButton
                type="button"
                className="avatar"
                shape="Circle"
                size="Small"
                disabled={!profile}
                {...profilePopoverProps.triggerProps}
                onClick={profilePopoverProps.toggle}
                ref={profilePopoverProps.triggerRef}
                name="avatar"
              >
                {profile ? (
                  <Avatar {...profile} variant="Person" className="icon" />
                ) : (
                  <Avatar firstName="?" variant="Person" className="icon" />
                )}
              </IconButton>

              <IconButton
                type="button"
                className="toggle md:d-none"
                aria-controls={navId}
                aria-expanded={expanded}
                aria-label="Toggle navigation"
                shape="Circle"
                size="Small"
                variant="StandardNavbar"
                onClick={onToggle}
              >
                <MenuIcon aria-hidden="true" width="24" height="24" />
              </IconButton>

              <IconButton
                type="button"
                className="toggle d-none md:d-unset"
                aria-label="Toggle navigation"
                shape="Circle"
                size="Small"
                {...linksPopoverProps.triggerProps}
                onClick={linksPopoverProps.toggle}
                ref={linksPopoverProps.triggerRef}
              >
                <MenuIcon aria-hidden="true" width="24" height="24" />
              </IconButton>

              {profile ? (
                <NavbarPopover
                  {...profilePopoverProps}
                  className={profilePopover}
                >
                  <NavbarProfileCard
                    {...profile}
                    onLinkClicked={onPopoverItemClicked}
                  />
                </NavbarPopover>
              ) : null}

              <NavbarPopover {...linksPopoverProps} className={linksPopover}>
                <nav>
                  <ul>
                    {secondaryLinks?.map(
                      ({ title, active, alwaysVisible, ...props }, index) => (
                        <li key={props.href ?? index}>
                          <NavLink
                            {...props}
                            onClick={(event) => {
                              props.onClick?.(event);
                              onPopoverItemClicked();
                            }}
                          >
                            {title}
                          </NavLink>
                        </li>
                      )
                    )}
                  </ul>
                  <hr />
                  <ul>
                    {tertiaryLinks?.map(({ title, active, ...props }) => (
                      <li key={props.href} className="tertiary">
                        <NavLink
                          {...props}
                          onClick={(event) => {
                            props.onClick?.(event);
                            onPopoverItemClicked();
                          }}
                        >
                          {title}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              </NavbarPopover>
            </div>
          )}
        </div>
      </header>
    </FocusScope>
  );
}
